import EmailIcon from '@mui/icons-material/Email'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    styled,
} from '@mui/material'
import { Stack } from '@mui/system'
import { useEffect, useRef } from 'react'
import { useBranch } from '../../context/branch_context'
import { useConfigs } from '../../context/configs_context'
import { DeliveryInfo } from '../../context/sale_context'
import { getDateString } from '../../utils/maks'

const StyledTableCell = styled(TableCell)(({}) => ({
    color: 'text.primary',
    border: 'none',
    padding: '1px 2px',
    fontSize: '11px',
}))

interface DeliveryNote {
    delivery: DeliveryInfo
}

const ContactIcon: Record<string, JSX.Element> = {
    Facebook: <FacebookIcon />,
    Instagram: <InstagramIcon />,
    Whatsapp: <WhatsAppIcon />,
    Endereço: <LocationOnIcon />,
    Telefone: <LocalPhoneIcon />,
    Email: <EmailIcon />,
}

function DeliveryNote({ delivery }: DeliveryNote) {
    const { configs, contactTypes, base64Logo } = useConfigs()
    const { branches } = useBranch()

    const contacts = delivery.sale?.branch
        ? delivery.sale.branch.contacts
        : configs.contacts
    const companyName =
        delivery.sale?.branch && configs.showBranchNameInNote
            ? delivery.sale?.branch.name
            : configs.companyName
    const companyLogo = delivery.sale?.branch?.companyLogo
        ? branches.find((b) => b.id === delivery.sale?.branch?.id)?.companyLogo
              ?.content
        : base64Logo
    const companySlogan = delivery.sale?.branch?.companySlogan
        ? delivery.sale?.branch.companySlogan
        : configs.companySlogan

    return (
        <Stack direction="column" width="100%" px="20px" py="10px">
            <Stack direction={'row'} width={'100%'}>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    width={'100%'}
                >
                    <Stack direction={'row'} p={'10px'}>
                        <Box
                            component={'img'}
                            src={companyLogo}
                            width={'220px'}
                            height={'110px'}
                            zIndex={'-1'}
                        />
                        <Stack textAlign={'center'} flex={'1'} px={'15px'}>
                            <Typography
                                fontSize={'14px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {companyName}
                            </Typography>
                            <Typography
                                fontSize={'9px'}
                                sx={{ wordBreak: 'break-word' }}
                                fontStyle={'italic'}
                                whiteSpace={'pre-line'}
                            >
                                {`${companySlogan}`}
                            </Typography>
                            <Typography
                                fontSize={'8px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {contactTypes
                                    .filter((ct) =>
                                        contacts.find(
                                            (c) => c.contactType.id === ct.id
                                        )
                                    )
                                    .map(
                                        (ct) =>
                                            `${ct.name}: ${contacts
                                                .filter(
                                                    (c) =>
                                                        c.contactType.id ===
                                                        ct.id
                                                )
                                                .map((c) => c.value)
                                                .join(', ')}`
                                    )
                                    .join('; ')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    width={'30%'}
                >
                    <Stack direction={'row'} p={'10px'}>
                        <Box flex={'1'}>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                Fecha:{' '}
                                {getDateString({
                                    date: delivery.date,
                                    hour: delivery.hour,
                                    configs: configs,
                                    isSummerTime: !!delivery.isSummerTime,
                                })}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                N.º Venta: {delivery.sale?.id}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                Vendedor: {delivery.sale?.employee?.name}
                            </Typography>
                            {configs.showWeightOnNote && (
                                <Typography fontSize={'11px'} mt={'1px'}>
                                    Peso total:{' '}
                                    {delivery.sale?.products.reduce(
                                        (total, curr) =>
                                            total +
                                            curr.product.realWeight * curr.qnt,
                                        0
                                    )}{' '}
                                    kg
                                </Typography>
                            )}
                            <Typography
                                fontSize={'13px'}
                                mt={'5px'}
                                fontWeight={'bold'}
                                textAlign={'center'}
                            >
                                Entrega n.º {delivery.id}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                direction={'column'}
                padding={'5px'}
            >
                <Stack direction={'row'} alignItems={'center'}>
                    <Box width={'70%'}>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            Nombre: {delivery.sale?.client?.name || '---'}
                        </Typography>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            Documento:{' '}
                            {delivery.sale?.client?.document || '---'}
                        </Typography>
                    </Box>
                    <Box>
                        <Box>
                            {/* <Typography fontSize={'11px'}>
                                Fecha:{' '}
                                {getDateString({
                                    date: delivery.date,
                                    hour: delivery.hour,
                                    configs: configs,
                                    isSummerTime: !!delivery.isSummerTime,
                                })}
                            </Typography> */}
                            <Typography fontSize={'11px'}>
                                Telefonos:{' '}
                                {delivery.sale?.client?.phoneNumbers.join(
                                    ', '
                                ) || '---'}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Dirección:{' '}
                                {delivery.sale?.client?.address || '---'}
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                direction={'column'}
                flex={'1'}
                justifyContent={'space-between'}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            {configs.showIdOnNote && (
                                <StyledTableCell
                                    sx={{
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    CÓDIGO
                                </StyledTableCell>
                            )}
                            {configs.showBarcodeOnNote && (
                                <StyledTableCell
                                    sx={{
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    CÓDIGO BAR.
                                </StyledTableCell>
                            )}
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                CANT. / DESC.
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                SERIAL
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                LOTE
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {delivery.products
                            .filter(
                                (product) =>
                                    product.qnt - product.refundedQnt > 0
                            )
                            .map((product) => (
                                <TableRow sx={{ height: '0' }}>
                                    {configs.showIdOnNote && (
                                        <StyledTableCell>
                                            {product.product?.internalCodes[0]}
                                        </StyledTableCell>
                                    )}
                                    {configs.showBarcodeOnNote && (
                                        <StyledTableCell>
                                            {product.product?.barcodes[0] ||
                                                '---'}
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell>
                                        {product.qnt - product.refundedQnt} /{' '}
                                        {product.product?.parsedName}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {product.serial || ''}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {product.lote || ''}
                                    </StyledTableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                px="10px"
                pt="20px"
                direction={'row'}
                spacing={'15px'}
            >
                <Typography fontSize="11px">
                    <Box component={'span'} fontWeight={'bold'}>
                        Firma:
                    </Box>{' '}
                    ______________________________
                </Typography>
                <Typography fontSize="11px">
                    <Box component={'span'} fontWeight={'bold'}>
                        Aclaración:
                    </Box>{' '}
                    ______________________________
                </Typography>
                <Typography fontSize="11px">
                    <Box component={'span'} fontWeight={'bold'}>
                        Fecha:
                    </Box>{' '}
                    ______________________________
                </Typography>
            </Stack>
        </Stack>
    )
}

interface Props {
    deliveries: DeliveryInfo[]
    handleClose(): void
    dontPrint?: boolean
}

export function DeliveryNoteDialog({
    handleClose,
    deliveries,
    dontPrint,
}: Props) {
    const { configs } = useConfigs()
    const printRef = useRef<HTMLElement>()

    useEffect(() => {
        if (!printRef.current) return

        const twoPages = !configs.printHalfPage || dontPrint

        for (let child in printRef.current.children) {
            if (isNaN(Number(child))) continue
            const element = printRef.current.children[child] as HTMLElement

            const a4HeightPx = 1122
            let heightInVh = (element.offsetHeight / a4HeightPx) * 100

            if (heightInVh < 50 && !twoPages) {
                heightInVh = 50
            } else {
                heightInVh = 100 * (Math.floor((heightInVh - 1) / 100) + 1)
            }

            element.style.height = `${heightInVh}vh`
        }

        if (!dontPrint) {
            printRef.current!.style.visibility = 'hidden'
            printRef.current!.style.position = 'fixed'

            setTimeout(() => {
                printRef.current!.style.position = 'absolute'
                printRef.current!.className = 'printable'
                window.scroll(0, 0)
                window.print()
                handleClose()
            }, 100)
        }
    }, [])

    return (
        <Box id="note" ref={printRef}>
            {deliveries.map((delivery) => (
                <>
                    {Array.from({
                        length: !dontPrint ? configs.deliveryNotesQnt : 1,
                    }).map(() => (
                        <DeliveryNote delivery={delivery} />
                    ))}
                </>
            ))}
        </Box>
    )
}
